import React, { useCallback, useMemo } from 'react';
import { Plus, Zap } from 'lucide-react';
import { Assert } from '@cotera/utilities';
import { Text } from '@cotera/client/app/components/ui';
import { ResourceType, coco as apiCoco } from '@cotera/api';
import {
  useCocoMentions,
  type MentionResourceKind,
} from '@cotera/client/app/hooks/use-coco-mentions';
import {
  ResourcePickerContent,
  buildCategoryIconMap,
  buildSubgroupIconMap,
  mentionOptionToPickerItem,
  type ResourcePickerItem,
} from '@cotera/client/app/components/resource-picker';
import {
  FLOW_CONTROL_ITEMS,
  flowControlSelection,
  type WorkflowNodePaletteSelection,
} from './workflow-node-palette';
import type { WorkflowNodePaletteBuiltinItem } from '../workflow-node-palette-registry';

/**
 * What picking a row does. Most rows add a node; two hand off to another
 * surface (the new-agent artifact, the trigger editor drawer) instead.
 */
type NodePickerAction =
  | { readonly t: 'add-node'; readonly selection: WorkflowNodePaletteSelection }
  | { readonly t: 'create-agent' }
  | { readonly t: 'create-workflow' }
  | { readonly t: 'add-trigger' };

type NodePickerItem = ResourcePickerItem<NodePickerAction>;

const CREATE_AGENT_ITEM_ID = '__create_agent__';
const CREATE_WORKFLOW_ITEM_ID = '__create_workflow__';
const ADD_TRIGGER_ITEM_ID = '__add_trigger__';

/**
 * Groups the builder can turn into a node. Held at module scope because it keys
 * the memo inside `useCocoMentions` that builds every option.
 */
const ADD_NODE_RESOURCE_KINDS: readonly MentionResourceKind[] = [
  ResourceType.Agent,
  ResourceType.Workflow,
  'tool',
];

/**
 * A loop body offers the same resources as any other step: the for-each fans
 * its items out to per-item instances and the body runs under the normal node
 * dispatch, so running another workflow once per item works like any node.
 */
const LOOP_BODY_RESOURCE_KINDS: readonly MentionResourceKind[] =
  ADD_NODE_RESOURCE_KINDS;

/**
 * Builtin steps a for-each body can't be. `for-each` would nest a loop, and
 * `emit-event`/`mapper` have no body form — all three are dropped by
 * `addForEachBodyNode`, so offering them would close the modal and do nothing.
 */
const LOOP_BODY_EXCLUDED_BUILTINS: readonly WorkflowNodePaletteBuiltinItem['kind'][] =
  ['for-each', 'emit-event', 'mapper'];

/**
 * Where the new step lands relative to the card the user started from:
 * `'after'` it, `'before'` it, or `null` when nothing is being connected.
 */
export type AddWorkflowNodeConnecting = 'after' | 'before' | null;

function headerFor(
  intent: 'add-node' | 'loop-body',
  connecting: AddWorkflowNodeConnecting
): {
  title: string;
  subtitle: string;
} {
  if (intent === 'loop-body') {
    return {
      title: 'Loop body',
      subtitle:
        'Runs once for each item. Pick an agent, tool, workflow, or filter.',
    };
  }
  return {
    title: 'Add node',
    subtitle:
      connecting === 'after'
        ? 'The new step runs after the node you selected.'
        : connecting === 'before'
        ? 'The new step runs before the node you selected.'
        : 'What do you want to add?',
  };
}

const flowControlItem = (
  item: WorkflowNodePaletteBuiltinItem
): NodePickerItem => {
  const ItemIcon = item.icon;
  return {
    id: `builtin:${item.id}`,
    label: item.label,
    category: 'flow-control',
    data: { t: 'add-node', selection: flowControlSelection(item) },
    color: 'teal',
    typeLabel: 'Flow control',
    icon: <ItemIcon />,
    searchKeywords: [...(item.keywords ?? []), 'flow control'],
    detail:
      item.description === undefined
        ? undefined
        : { title: item.label, summary: item.description },
  };
};

/**
 * Add-node picker. One searchable list built from the same `useCocoMentions`
 * options that back the editor's `@` menu, so agents, workflows and tools read
 * and rank identically in both places. The builder's own steps (flow control,
 * plus the two hand-offs) are appended as extra rows.
 *
 * `intent: 'loop-body'` reuses it to choose the for-each child step: no
 * workflows, no nested loop, and copy adjusted for the loop context.
 */
export const AddWorkflowNodePicker: React.FC<{
  canAddTrigger: boolean;
  connecting: AddWorkflowNodeConnecting;
  onSelect: (selection: WorkflowNodePaletteSelection) => void;
  onAddTrigger: () => void;
  onCreateAgent: () => void;
  onCreateWorkflow: () => void;
  /** Closes the modal without adding anything (escape). */
  onDismiss: () => void;
  intent?: 'add-node' | 'loop-body';
  /** Current workflow id — excluded from the Workflows group. */
  excludeWorkflowId?: string;
}> = ({
  canAddTrigger,
  connecting,
  onSelect,
  onAddTrigger,
  onCreateAgent,
  onCreateWorkflow,
  onDismiss,
  intent = 'add-node',
  excludeWorkflowId,
}) => {
  const isLoopBody = intent === 'loop-body';
  const { title, subtitle } = headerFor(intent, connecting);

  const { options, groups, isLoading, enableSearch } = useCocoMentions({
    resourceTypes: isLoopBody
      ? LOOP_BODY_RESOURCE_KINDS
      : ADD_NODE_RESOURCE_KINDS,
    // Chat-only tools throw when a ToolCall node runs them, so keep them out.
    toolContext: 'workflow',
    enableSearch: true,
  });

  const categoryIcons = useMemo(() => buildCategoryIconMap(groups), [groups]);
  const providerIcons = useMemo(() => buildSubgroupIconMap(groups), [groups]);

  const items = useMemo((): NodePickerItem[] => {
    const resourceItems: NodePickerItem[] = [];

    for (const option of options) {
      const base = mentionOptionToPickerItem(option);
      if (base.category === 'agent') {
        resourceItems.push({
          ...base,
          data: {
            t: 'add-node',
            selection: {
              kind: 'agent',
              agentId: base.id,
              agentName: base.label,
            },
          },
        });
        continue;
      }
      if (base.category === 'workflow') {
        const targetWorkflowId = apiCoco.workflowIdFromToolKey(base.id);
        // A workflow can't call itself, and a key we can't decode back to an id
        // can't be turned into a node.
        if (
          targetWorkflowId === null ||
          targetWorkflowId === excludeWorkflowId
        ) {
          continue;
        }
        resourceItems.push({
          ...base,
          data: {
            t: 'add-node',
            selection: {
              kind: 'run-workflow',
              targetWorkflowId,
              workflowName: base.label,
            },
          },
        });
        continue;
      }
      if (base.category === 'tool') {
        resourceItems.push({
          ...base,
          data: {
            t: 'add-node',
            selection: {
              kind: 'tool-call',
              toolKey: base.id,
              toolName: base.label,
            },
          },
        });
      }
    }

    const createAgent: NodePickerItem = {
      id: CREATE_AGENT_ITEM_ID,
      label: 'Create new agent',
      category: 'agent',
      data: { t: 'create-agent' },
      icon: <Plus />,
      typeLabel: 'Agent',
      searchKeywords: ['new', 'create', 'agent'],
      detail: {
        title: 'Create new agent',
        summary: 'Open a new agent artifact to configure from scratch.',
      },
    };

    const createWorkflow: NodePickerItem = {
      id: CREATE_WORKFLOW_ITEM_ID,
      label: 'Create new workflow',
      category: 'workflow',
      data: { t: 'create-workflow' },
      icon: <Plus />,
      typeLabel: 'Workflow',
      searchKeywords: ['new', 'create', 'workflow'],
      detail: {
        title: 'Create new workflow',
        summary: 'Open a new workflow artifact to build from scratch.',
      },
    };

    const addTrigger: NodePickerItem = {
      id: ADD_TRIGGER_ITEM_ID,
      label: 'Add a trigger',
      category: 'trigger',
      data: { t: 'add-trigger' },
      icon: <Zap />,
      typeLabel: 'Trigger',
      searchKeywords: ['trigger', 'schedule', 'cron', 'webhook', 'event'],
      detail: {
        title: 'Add a trigger',
        summary: 'Start this workflow on a schedule or incoming event.',
      },
    };

    const builtins = FLOW_CONTROL_ITEMS.filter(
      (item) => !isLoopBody || !LOOP_BODY_EXCLUDED_BUILTINS.includes(item.kind)
    ).map(flowControlItem);

    // Order within a category is preserved, so "Create new agent" leads the
    // Agents group the way it led the old agent page.
    return [
      createAgent,
      createWorkflow,
      ...resourceItems,
      ...builtins,
      ...(canAddTrigger && !isLoopBody ? [addTrigger] : []),
    ];
  }, [options, excludeWorkflowId, isLoopBody, canAddTrigger]);

  const handleSelect = useCallback(
    (item: NodePickerItem): void => {
      switch (item.data.t) {
        case 'add-node':
          onSelect(item.data.selection);
          return;
        case 'create-agent':
          onCreateAgent();
          return;
        case 'create-workflow':
          onCreateWorkflow();
          return;
        case 'add-trigger':
          onAddTrigger();
          return;
        default:
          return Assert.unreachable(item.data);
      }
    },
    [onSelect, onCreateAgent, onCreateWorkflow, onAddTrigger]
  );

  return (
    <div className="flex w-full min-h-0 flex-col">
      <div className="flex flex-col">
        <Text.Title className="mb-1" type="section">
          {title}
        </Text.Title>
        <span className="text-sm text-muted-foreground">{subtitle}</span>
      </div>
      <ResourcePickerContent
        items={items}
        mode="single"
        onSelect={handleSelect}
        onDismiss={onDismiss}
        isLoading={isLoading}
        showSearchInput={enableSearch}
        categoryIcons={categoryIcons}
        providerIcons={providerIcons}
        searchPlaceholder="Search agents, tools, and steps…"
        // The modal already draws the card, so the picker contributes only its
        // list; it fills the space the modal's max-height leaves it. The
        // negative inset cancels the picker's own row padding so the search
        // box, tabs and rows share the header's left edge.
        className="-mx-3 min-h-0 flex-1 rounded-none border-0 bg-transparent shadow-none"
        maxHeight="min(60vh, 520px)"
      />
    </div>
  );
};
